import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { Constants, transactionDetailLabels } from '../../../shared/utilities/Constants';
import { alphaNumericChars, beneficiaryAccountNumberAllowedChars, beneficiaryAccountNumberNotAllowedChars } from '../../../shared/utilities/regex-patterns';
import { createControl } from '../../utilities/formUtilities';
import { selectUseStandingAuth, selectWireInfo } from '../../reducers';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { selectStandingAuthWireInfo } from '../../../standing-authorizations/reducers';

@Component({
  standalone: false,
  selector: 'mm-wire-account-number',
  templateUrl: './wire-account-number.component.html',
  styleUrls: ['./wire-account-number.component.scss']
})
export class WireAccountNumberComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() useSloa: boolean;
  @Output() wireAccountNumber = new EventEmitter<string>();
  private beneficiaryAccountControlName = 'toAccount';
  private receivingAccountHolderAccountNumber = 'receivingAccountHolderAccountNumber';
  accountNumberMaxLength: number = Constants.beneficiaryAccountNumberMaxLength;
  toAccountAllowedChars: RegExp = beneficiaryAccountNumberAllowedChars;
  toAccountError: string = Constants.beneficiaryAccountNumberMissing;
  destroy$: Subject<boolean> = new Subject<boolean>();
  useSloa$: Observable<boolean>;
  isIrsAba: boolean = false;

  constructor(public validationService: ValidateInputService, private fb: UntypedFormBuilder, private store: Store) { }

  ngOnInit(): void {
    this.useSloa$ = this.store.select(selectUseStandingAuth);
    combineLatest([this.store.select(selectWireInfo), this.store.select(selectStandingAuthWireInfo)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([wireInfo, standingAuthWireInfo]) => {
        this.isIrsAba = wireInfo.isIrsAba || standingAuthWireInfo.isIrsAba;
      });
    createControl(
      this.parentForm,
      this.useSloa ? this.receivingAccountHolderAccountNumber : this.beneficiaryAccountControlName,
      this.useSloa ? this.toAccount?.value : '',
      this.isIrsAba ? undefined : [
        Validators.required,
        Validators.maxLength(Constants.beneficiaryAccountNumberMaxLength),
      ],
      undefined,
      this.useSloa
    );
    if (!this.parentForm.get('receivingAccountHolderIdentifierList')) {
    this.parentForm.addControl('receivingAccountHolderIdentifierList', new UntypedFormControl([]));
  }
    this.useSloa ? this.wireAccountNumber.emit(this.toAccount?.value) : this.wireAccountNumber.emit(undefined);
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl(this.receivingAccountHolderAccountNumber);
    this.parentForm?.removeControl(this.beneficiaryAccountControlName);
  }

  get accountNumberModel(): TextInputModel {
    return {
      controlName: this.useSloa ? this.receivingAccountHolderAccountNumber : this.beneficiaryAccountControlName,
      label: this.getAccountNumberModelLabel(),
      hasGreyText: false,
      allowedChars: this.toAccountAllowedChars,
      htmlMaxLength: this.accountNumberMaxLength,
    };
  }

  getAccountNumberModelLabel(): string {
    if(this.parentForm?.value?.receivingAccountHolderIdentifierList !== undefined && this.parentForm?.value?.receivingAccountHolderIdentifierList !== null) {
      return this.parentForm?.value?.receivingAccountHolderIdentifierList[0]?.identifierType?.toLowerCase() === 'iban' ? transactionDetailLabels.isoReceivingIBAN : transactionDetailLabels.isoReceivingBankAccountNumber;
    } else {
      return transactionDetailLabels.receivingBankAccountNumber;
    }
  }

  get toAccount() {
     return this.useSloa? this.parentForm?.get(this.receivingAccountHolderAccountNumber) as UntypedFormControl : this.parentForm?.get(this.beneficiaryAccountControlName) as UntypedFormControl;
  }

  clearToAccount() {
    this.parentForm.controls.toAccount.setValue('');
    this.wireAccountNumber.emit(this.toAccount.value);
  }

  onInput() {
    const formattedAccountNumber = this.toAccount.value.replace(beneficiaryAccountNumberNotAllowedChars, '');
    this.toAccount.setValue(formattedAccountNumber);
    this.wireAccountNumber.emit(this.toAccount.value);
  }

  onBlur() {
    this.toAccount?.setValue(this.toAccount?.value?.trim());
    let toAccount: string = this.toAccount.value;
    if (!toAccount?.charAt(0).match(alphaNumericChars)) {
      this.toAccount.setValue(this.formatToAccount(toAccount));
      return;
    }
    this.toAccount.setValue(toAccount);
    this.wireAccountNumber.emit(this.toAccount.value);
  }

  onClick() {
    this.wireAccountNumber.emit(this.toAccount.value);
  }

  // Removes special characters from beginning of string until alphanumeric char is encountered
  formatToAccount(toAccount: string) {
    let replacementStr = toAccount;
    for (let i = 0; i < toAccount.length; i++) {
      if (!toAccount.charAt(i).match(alphaNumericChars)) {
        replacementStr = replacementStr.replace(toAccount.charAt(i), '');
      } else {
        break;
      }
    }
    return replacementStr;
  }
}
